import i18n from '../../../i18n';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from "react-router-dom";
import httpService from "../../../services/http.service";

/** Petits éléments partagés par la liste des signalements et leur fiche. */

export const formatReportDate = (value) => value
    ? new Date(value).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {dateStyle: 'short', timeStyle: 'short'})
    : '';

export const StateBadge = ({state}) => {
    const {t} = useTranslation();
    if (state === 'approved') return <span className="badge badge-success ml-0">{t("Validé")}</span>;
    if (state === 'rejected') return <span className="badge badge-secondary ml-0">{t("Rejeté")}</span>;
    return <span className="badge badge-warning ml-0">{t("En attente")}</span>;
};

export const TypeBadge = ({type}) => {
    const {t} = useTranslation();
    if (type === 'comment') return <span className="badge badge-info ml-0">{t("Commentaire")}</span>;
    if (type === 'user') return <span className="badge badge-dark ml-0">{t("Utilisateur")}</span>;
    return <span className="badge badge-primary ml-0">{t("Opinion")}</span>;
};

/** Motifs ; `max` limite le nombre de badges affichés (tableau), le reste est résumé en « +N » avec infobulle. */
export const ReasonBadges = ({reasons = [], max}) => {
    const shown = max ? reasons.slice(0, max) : reasons;
    const hidden = reasons.slice(shown.length);
    if (max) {
        // Tableau : un motif par ligne, tronqué dans la largeur de sa colonne (rien ne déborde sur la suivante).
        return (
            <div className="d-flex flex-column align-items-start" style={{minWidth: 0, overflow: 'hidden'}}>
                {shown.map(reason => (
                    <span key={reason} className="badge badge-light border mb-1 text-truncate" style={{maxWidth: '100%'}} title={reason}>{reason}</span>
                ))}
                {hidden.length > 0 && <span className="badge badge-secondary ml-0" title={hidden.join(', ')}>+{hidden.length}</span>}
            </div>
        );
    }
    return (
        <>
            {shown.map(reason => (
                <span key={reason} className="badge badge-light border mr-1 mb-1" title={reason}>{reason}</span>
            ))}
        </>
    );
};

/** Vignette du tableau : seulement le média principal de l'opinion (les pièces jointes sont dans la fiche). */
export const MediaHint = ({media: all = []}) => {
    const media = all.filter(item => item.source !== 'attachment');
    if (!media.length) return null;
    const first = media[0];
    const preview = first.type === 'image' ? first.url : first.thumbnail;
    return (
        <span className="d-inline-flex align-items-center mr-2" title={`${media.length} média(s)`}>
            {preview ? (
                <img src={preview} alt="" style={{width: 36, height: 36, objectFit: 'cover', borderRadius: 6}} />
            ) : (
                <span className="d-inline-flex align-items-center justify-content-center bg-light rounded" style={{width: 36, height: 36}}>
                    <i className={`fa ${first.type === 'video' ? 'fa-video-camera' : first.type === 'audio' ? 'fa-music' : 'fa-paperclip'}`}></i>
                </span>
            )}
            {media.length > 1 && <small className="text-muted ml-1">+{media.length - 1}</small>}
        </span>
    );
};

/** Médias en grand dans la fiche : images (ouvrables), vidéos et sons lisibles, autres fichiers en lien. */
export const MediaGallery = ({media = []}) => {
    if (!media.length) return null;
    return (
        <div className="d-flex flex-wrap mt-2" style={{gap: 8}}>
            {media.map((item, index) => {
                if (item.type === 'image') {
                    return (
                        <a key={index} href={item.url} target="_blank" rel="noopener noreferrer">
                            <img src={item.url} alt="" style={{width: 150, height: 150, objectFit: 'cover', borderRadius: 8}} />
                        </a>
                    );
                }
                if (item.type === 'video') {
                    return <video key={index} src={item.url} poster={item.thumbnail || undefined} controls preload="metadata" style={{width: '100%', maxHeight: 320, borderRadius: 8, background: '#000'}} />;
                }
                if (item.type === 'audio') {
                    return <audio key={index} src={item.url} controls preload="none" style={{width: '100%'}} />;
                }
                return (
                    <a key={index} href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                        <i className="fa fa-paperclip"></i>{' '}{item.url.split('/').pop()}
                    </a>
                );
            })}
        </div>
    );
};

/** Utilisateur de l'application : lien vers sa fiche par nom d'utilisateur (jamais par id). */
export const UserCell = ({user}) => {
    const {t} = useTranslation();
    if (!user) return <span className="text-muted small">{t("Compte supprimé")}</span>;
    return (
        <Link to={`/users/${encodeURIComponent(user.user_username)}`} onClick={(e) => e.stopPropagation()} className="text-nowrap">
            {user.user_full_name || user.user_username}
            <div className="text-muted small">@{user.user_username}{user.user_blocked ? ` · ${t("bloqué")}` : ''}</div>
        </Link>
    );
};

/**
 * Valide (approve) ou rejette (reject) un signalement après confirmation ; le signaleur est prévenu par e-mail.
 * Renvoie le signalement mis à jour, ou null (annulé ou refusé : message affiché).
 */
export const decideReport = async (report, decision) => {
    const message = decision === 'approve'
        ? i18n.t("Valider ce signalement ? Le signaleur sera prévenu par e-mail.")
        : i18n.t("Rejeter ce signalement ? Le signaleur sera prévenu par e-mail.");
    if (!window.confirm(message)) return null;

    try {
        const {data} = await httpService.post(`/reports/${report.report_id}/${decision === 'approve' ? 'confirm' : 'reject'}`);
        return data?.data || null;
    } catch (e) {
        window.alert(e?.response?.data?.message || i18n.t("Action impossible, veuillez réessayer."));
        return null;
    }
};
